// FertilizerPlanPage.jsx
// Styled MUI Mockup (mehr Farbe + moderne Card-Optik, theme-kompatibel)
// Beispiel in FertilizerPlanPage.jsx
import { fertApi as defaultFertApi } from "../api/duengeplanApi";

import { useGrowSetup } from '../GrowSetupContext';
import React, { useMemo, useState,useEffect } from "react";
import {
  Box,
  Grid,
  Card,
  CardContent,
  CardHeader,
  Typography,
  Stack,
  Chip,
  Divider,
  Tabs,
  Tab,
  Button,
  IconButton,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  FormGroup,
  FormControlLabel,
  Switch,
  Checkbox,
  List,
  ListItemButton,
  ListItemText,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  InputAdornment,
  Tooltip,
  Badge,
} from "@mui/material";
import { alpha, useTheme } from "@mui/material/styles";

import WaterDropIcon from "@mui/icons-material/WaterDrop";
import ScienceIcon from "@mui/icons-material/Science";
import StraightenIcon from "@mui/icons-material/Straighten";
import EditIcon from "@mui/icons-material/Edit";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import CloseIcon from "@mui/icons-material/Close";
import CalendarMonthIcon from "@mui/icons-material/CalendarMonth";
import ViewTimelineIcon from "@mui/icons-material/ViewTimeline";
import TableRowsIcon from "@mui/icons-material/TableRows";
import AddIcon from "@mui/icons-material/Add";
import DeleteIcon from "@mui/icons-material/Delete";


const PRODUCT_BUY_LINKS = [
  {
    match: "Green Grower Wuchsdünger",
    url: "https://shop.green-grower.de/produkt/green-grower-wuchsduenger-starkes-wachstum-von-anfang-an/",
  },
  {
    match: "Green Grower Blütedünger",
    url: "https://shop.green-grower.de/produkt/blueteduenger/",
  },
  {
    match: "CalMag",
    url: "https://shop.green-grower.de/produkt/calmag-calcium-magnesium-booster-green-grower-calmag/",
  },
  // ...hier beliebig ergänzen
];

/** -----------------------------
 *  Mock-Daten (später ersetzen durch API/Store)
 *  ----------------------------- */
const MOCK_GROW_SETUPS = [
  { id: "gs_001", name: "Grow Setup #1 (Zelt 80x80)" },
  { id: "gs_002", name: "Grow Setup #2 (Box)" },
];

const MOCK_TEMPLATES = [
  { id: "tpl_gg_basis", name: "GG Basis", description: "Standard für Erde/Allround" },
  { id: "tpl_light_feed", name: "Light Feed", description: "Sanfte Dosierung, stressarm" },
];

const MOCK_PHASES = ["Keimung", "Veg", "Blüte", "Flush"];

const EVENT_TYPES = [
  { key: "watering", label: "Gießen", icon: <WaterDropIcon fontSize="small" /> },
  { key: "fertilizing", label: "Düngen", icon: <ScienceIcon fontSize="small" /> },
  { key: "measurement", label: "Messung", icon: <StraightenIcon fontSize="small" /> },
  { key: "flush", label: "Spülen", icon: <WaterDropIcon fontSize="small" /> },
];

const MOCK_EVENTS = [
  {
    id: "ev_1001",
    grow_setup_id: "gs_001",
    date_iso: "2025-12-31T18:00:00",
    type: "fertilizing",
    phase: "Blüte",
    week_index: 4,
    water_l: 3.0,
    targets: { ec: 1.6, ph: 6.2 },
    recipe_lines: [
      { id: "rl_1", product_name: "Blüte A", ml_per_l: 2.0 },
      { id: "rl_2", product_name: "Blüte B", ml_per_l: 2.0 },
      { id: "rl_3", product_name: "CalMag", ml_per_l: 0.5 },
    ],
    actuals: { ec: null, ph: null },
    note: "",
    done: false,
  },
  {
    id: "ev_1002",
    grow_setup_id: "gs_001",
    date_iso: "2026-01-02T18:00:00",
    type: "measurement",
    phase: "Blüte",
    week_index: 4,
    water_l: null,
    targets: { ec: 1.6, ph: 6.2 },
    recipe_lines: [],
    actuals: { ec: 1.7, ph: 6.1 },
    note: "Blätter leicht dunkler – beobachten",
    done: true,
  },
  {
    id: "ev_2001",
    grow_setup_id: "gs_002",
    date_iso: "2025-12-30T19:00:00",
    type: "watering",
    phase: "Veg",
    week_index: 2,
    water_l: 2.5,
    targets: { ec: 1.1, ph: 6.3 },
    recipe_lines: [],
    actuals: { ec: 1.0, ph: 6.4 },
    note: "",
    done: false,
  },
];

/** -----------------------------
 *  Helper
 *  ----------------------------- */
function formatDateTimeLocal(iso) {
  const d = new Date(iso);
  const pad = (n) => String(n).padStart(2, "0");
  return `${pad(d.getDate())}.${pad(d.getMonth() + 1)}.${d.getFullYear()} ${pad(d.getHours())}:${pad(
    d.getMinutes()
  )}`;
}

function getBuyUrl(productName) {
  const name = (productName || "").toLowerCase();
  const hit = PRODUCT_BUY_LINKS.find((p) => name.includes((p.match || "").toLowerCase()));
  return hit?.url || null;
}

function getTypeMeta(typeKey) {
  return EVENT_TYPES.find((t) => t.key === typeKey) ?? { key: typeKey, label: typeKey, icon: null };
}

function computeRecipeTotals(recipeLines, waterL) {
  const water = Number(waterL || 0);
  return recipeLines.map((line) => ({
    ...line,
    total_ml: water > 0 ? Number((line.ml_per_l * water).toFixed(2)) : 0,
  }));
}

function getTypeAccent(theme, typeKey) {
  // Einfache, aber “farbige” Zuordnung (theme-kompatibel)
  switch (typeKey) {
    case "watering":
      return theme.palette.info.main;
    case "fertilizing":
      return theme.palette.success.main;
    case "measurement":
      return theme.palette.secondary.main;
    case "flush":
      return theme.palette.warning.main;
    default:
      return theme.palette.primary.main;
  }
}

function createCardFrameSx(theme) {
  return {
    borderRadius: 3,
    border: `1px solid ${alpha(theme.palette.divider, 0.9)}`,
    background: alpha(theme.palette.background.paper, 0.85),
    backdropFilter: "blur(6px)",
    boxShadow: `0 10px 30px ${alpha(theme.palette.common.black, 0.08)}`,
    transition: "transform 140ms ease, box-shadow 140ms ease, border-color 140ms ease",
    "&:hover": {
      transform: "translateY(-1px)",
      boxShadow: `0 16px 46px ${alpha(theme.palette.common.black, 0.12)}`,
      borderColor: alpha(theme.palette.primary.main, 0.45),
    },
  };
}

function createSectionGlowSx(theme, accent) {
  return {
    position: "relative",
    overflow: "hidden",
    "&::before": {
      content: '""',
      position: "absolute",
      inset: 0,
      pointerEvents: "none",
      background: `radial-gradient(600px 160px at 20% 0%, ${alpha(accent, 0.18)}, transparent 55%)`,
    },
  };
}

/** -----------------------------
 *  Dialog: Task erstellen/bearbeiten
 *  ----------------------------- */
function FertilizerTaskDialog({
  isTaskDialogOpen,
  onCloseTaskDialog,
  taskDraft,
  setTaskDraft,
  onSaveTaskDraft,
}) {
  const theme = useTheme();
  const frame = createCardFrameSx(theme);

  const isEdit = Boolean(taskDraft?.id);

  const recipeWithTotals = useMemo(
    () => computeRecipeTotals(taskDraft.recipe_lines || [], taskDraft.water_l),
    [taskDraft.recipe_lines, taskDraft.water_l]
  );

  const handleUpdateField = (fieldName, value) => {
    setTaskDraft((prev) => ({ ...prev, [fieldName]: value }));
  };

  const handleUpdateTargets = (targetKey, value) => {
    setTaskDraft((prev) => ({ ...prev, targets: { ...(prev.targets || {}), [targetKey]: value } }));
  };

  const handleUpdateActuals = (actualKey, value) => {
    setTaskDraft((prev) => ({ ...prev, actuals: { ...(prev.actuals || {}), [actualKey]: value } }));
  };

  const handleUpdateRecipeLine = (lineId, patch) => {
    setTaskDraft((prev) => ({
      ...prev,
      recipe_lines: (prev.recipe_lines || []).map((l) => (l.id === lineId ? { ...l, ...patch } : l)),
    }));
  };

  const handleAddRecipeLine = () => {
    setTaskDraft((prev) => ({
      ...prev,
      recipe_lines: [
        ...(prev.recipe_lines || []),
        { id: `rl_${Math.random().toString(16).slice(2)}`, product_name: "", ml_per_l: 0 },
      ],
    }));
  };

  const handleRemoveRecipeLine = (lineId) => {
    setTaskDraft((prev) => ({
      ...prev,
      recipe_lines: (prev.recipe_lines || []).filter((l) => l.id !== lineId),
    }));
  };

  return (
    <Dialog
      open={isTaskDialogOpen}
      onClose={onCloseTaskDialog}
      fullWidth
      maxWidth="md"
      data-testid="fertPlanTaskDialog"
      PaperProps={{
        sx: {
          borderRadius: 3,
          ...createSectionGlowSx(theme, theme.palette.primary.main),
          background: alpha(theme.palette.background.paper, 0.92),
          backdropFilter: "blur(10px)",
        },
      }}
    >
      <DialogTitle sx={{ pr: 6 }} data-testid="fertPlanTaskDialogTitle">
        {isEdit ? "Dünge-Task bearbeiten" : "Neuen Dünge-Task erstellen"}
        <IconButton
          onClick={onCloseTaskDialog}
          sx={{ position: "absolute", right: 8, top: 8 }}
          aria-label="Dialog schließen"
          data-testid="fertPlanTaskDialogCloseBtn"
        >
          <CloseIcon />
        </IconButton>
      </DialogTitle>

      <DialogContent dividers data-testid="fertPlanTaskDialogContent">
        <Grid container spacing={2}>
          <Grid item xs={12} md={6}>
            <TextField
              label="Datum / Uhrzeit"
              type="datetime-local"
              fullWidth
              value={taskDraft.date_iso_local || ""}
              onChange={(e) => handleUpdateField("date_iso_local", e.target.value)}
              inputProps={{ "data-testid": "fertPlanTaskFieldDateTime" }}
              helperText="Später: wird zu date_iso (ISO) konvertiert"
              sx={{ "& .MuiOutlinedInput-root": { borderRadius: 2 } }}
            />
          </Grid>

          <Grid item xs={12} md={6}>
            <FormControl fullWidth data-testid="fertPlanTaskTypeSelect">
              <InputLabel id="fertPlanTaskTypeSelectLabel">Typ</InputLabel>
              <Select
                labelId="fertPlanTaskTypeSelectLabel"
                label="Typ"
                value={taskDraft.type || "watering"}
                onChange={(e) => handleUpdateField("type", e.target.value)}
                sx={{ borderRadius: 2 }}
              >
                {EVENT_TYPES.map((t) => (
                  <MenuItem key={t.key} value={t.key}>
                    {t.label}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>

          <Grid item xs={12} md={6}>
            <TextField
              label="Wassermenge"
              fullWidth
              value={taskDraft.water_l ?? ""}
              onChange={(e) => handleUpdateField("water_l", e.target.value === "" ? "" : Number(e.target.value))}
              InputProps={{ endAdornment: <InputAdornment position="end">L</InputAdornment> }}
              inputProps={{ inputMode: "decimal", "data-testid": "fertPlanTaskFieldWaterLiters" }}
              helperText="Nur nötig für Gießen/Düngen/Spülen"
              sx={{ "& .MuiOutlinedInput-root": { borderRadius: 2 } }}
            />
          </Grid>

          <Grid item xs={12} md={6}>
            <FormControl fullWidth data-testid="fertPlanTaskPhaseSelect">
              <InputLabel id="fertPlanTaskPhaseSelectLabel">Phase</InputLabel>
              <Select
                labelId="fertPlanTaskPhaseSelectLabel"
                label="Phase"
                value={taskDraft.phase || "Veg"}
                onChange={(e) => handleUpdateField("phase", e.target.value)}
                sx={{ borderRadius: 2 }}
              >
                {MOCK_PHASES.map((p) => (
                  <MenuItem key={p} value={p}>
                    {p}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>

          <Grid item xs={12} md={6}>
            <TextField
              label="Ziel EC"
              fullWidth
              value={taskDraft.targets?.ec ?? ""}
              onChange={(e) => handleUpdateTargets("ec", e.target.value === "" ? "" : Number(e.target.value))}
              inputProps={{ inputMode: "decimal", "data-testid": "fertPlanTaskFieldTargetEC" }}
              sx={{ "& .MuiOutlinedInput-root": { borderRadius: 2 } }}
            />
          </Grid>
          <Grid item xs={12} md={6}>
            <TextField
              label="Ziel pH"
              fullWidth
              value={taskDraft.targets?.ph ?? ""}
              onChange={(e) => handleUpdateTargets("ph", e.target.value === "" ? "" : Number(e.target.value))}
              inputProps={{ inputMode: "decimal", "data-testid": "fertPlanTaskFieldTargetPH" }}
              sx={{ "& .MuiOutlinedInput-root": { borderRadius: 2 } }}
            />
          </Grid>

          <Grid item xs={12}>
            <Card variant="outlined" data-testid="fertPlanTaskRecipeCard" sx={{ ...frame }}>
              <CardHeader
                title="Rezept (Produkte)"
                subheader="ml/L + automatische Gesamtmenge (ml) anhand Wassermenge"
                action={
                  <Button
                    onClick={handleAddRecipeLine}
                    startIcon={<AddIcon />}
                    variant="contained"
                    size="small"
                    data-testid="fertPlanTaskRecipeAddLineBtn"
                    sx={{ borderRadius: 2 }}
                  >
                    Produkt hinzufügen
                  </Button>
                }
              />
              <CardContent>
                {recipeWithTotals.length === 0 ? (
                  <Typography variant="body2" color="text.secondary" data-testid="fertPlanTaskRecipeEmptyHint">
                    Noch keine Produkte hinzugefügt.
                  </Typography>
                ) : (
                  <Stack spacing={1} data-testid="fertPlanTaskRecipeLines">
                    {recipeWithTotals.map((line) => (
                      <Grid container spacing={1} key={line.id} alignItems="center">
                        <Grid item xs={12} md={6}>
                          <TextField
                            label="Produktname"
                            fullWidth
                            value={line.product_name}
                            onChange={(e) => handleUpdateRecipeLine(line.id, { product_name: e.target.value })}
                            inputProps={{ "data-testid": `fertPlanTaskRecipeProductName_${line.id}` }}
                            sx={{ "& .MuiOutlinedInput-root": { borderRadius: 2 } }}
                          />
                        </Grid>
                        <Grid item xs={6} md={3}>
                          <TextField
                            label="ml/L"
                            fullWidth
                            value={line.ml_per_l}
                            onChange={(e) =>
                              handleUpdateRecipeLine(line.id, {
                                ml_per_l: e.target.value === "" ? "" : Number(e.target.value),
                              })
                            }
                            inputProps={{ inputMode: "decimal", "data-testid": `fertPlanTaskRecipeMlPerL_${line.id}` }}
                            sx={{ "& .MuiOutlinedInput-root": { borderRadius: 2 } }}
                          />
                        </Grid>
                        <Grid item xs={4} md={2}>
                          <TextField
                            label="Gesamt ml"
                            fullWidth
                            value={line.total_ml}
                            InputProps={{ readOnly: true }}
                            inputProps={{ "data-testid": `fertPlanTaskRecipeTotalMl_${line.id}` }}
                            sx={{
                              "& .MuiOutlinedInput-root": { borderRadius: 2 },
                              "& .MuiInputBase-input.Mui-disabled": { WebkitTextFillColor: theme.palette.text.primary },
                            }}
                          />
                        </Grid>
                        <Grid item xs={2} md={1}>
                          <Tooltip title="Zeile entfernen">
                            <IconButton
                              onClick={() => handleRemoveRecipeLine(line.id)}
                              aria-label="Rezeptzeile löschen"
                              data-testid={`fertPlanTaskRecipeRemoveLineBtn_${line.id}`}
                              sx={{
                                borderRadius: 2,
                                border: `1px solid ${alpha(theme.palette.divider, 0.8)}`,
                                "&:hover": { borderColor: alpha(theme.palette.error.main, 0.6) },
                              }}
                            >
                              <DeleteIcon />
                            </IconButton>
                          </Tooltip>
                        </Grid>
                      </Grid>
                    ))}
                  </Stack>
                )}
              </CardContent>
            </Card>
          </Grid>

          <Grid item xs={12}>
            <Card variant="outlined" data-testid="fertPlanTaskActualsCard" sx={{ ...frame }}>
              <CardHeader title="Logbuch (IST-Werte & Notiz)" subheader="Optional – für Nachverfolgung" />
              <CardContent>
                <Grid container spacing={2}>
                  <Grid item xs={12} md={3}>
                    <TextField
                      label="IST EC"
                      fullWidth
                      value={taskDraft.actuals?.ec ?? ""}
                      onChange={(e) => handleUpdateActuals("ec", e.target.value === "" ? "" : Number(e.target.value))}
                      inputProps={{ inputMode: "decimal", "data-testid": "fertPlanTaskFieldActualEC" }}
                      sx={{ "& .MuiOutlinedInput-root": { borderRadius: 2 } }}
                    />
                  </Grid>
                  <Grid item xs={12} md={3}>
                    <TextField
                      label="IST pH"
                      fullWidth
                      value={taskDraft.actuals?.ph ?? ""}
                      onChange={(e) => handleUpdateActuals("ph", e.target.value === "" ? "" : Number(e.target.value))}
                      inputProps={{ inputMode: "decimal", "data-testid": "fertPlanTaskFieldActualPH" }}
                      sx={{ "& .MuiOutlinedInput-root": { borderRadius: 2 } }}
                    />
                  </Grid>
                  <Grid item xs={12} md={6}>
                    <TextField
                      label="Notiz"
                      fullWidth
                      value={taskDraft.note || ""}
                      onChange={(e) => handleUpdateField("note", e.target.value)}
                      inputProps={{ "data-testid": "fertPlanTaskFieldNote" }}
                      sx={{ "& .MuiOutlinedInput-root": { borderRadius: 2 } }}
                    />
                  </Grid>
                </Grid>
              </CardContent>
            </Card>
          </Grid>

          <Grid item xs={12}>
            <FormControlLabel
              control={
                <Switch
                  checked={Boolean(taskDraft.done)}
                  onChange={(e) => handleUpdateField("done", e.target.checked)}
                  inputProps={{ "data-testid": "fertPlanTaskFieldDoneSwitch" }}
                />
              }
              label="Als erledigt markieren"
              data-testid="fertPlanTaskDoneToggle"
              sx={{
                mt: 0.5,
                px: 1,
                py: 0.5,
                borderRadius: 2,
                background: alpha(theme.palette.primary.main, 0.07),
                border: `1px solid ${alpha(theme.palette.primary.main, 0.16)}`,
              }}
            />
          </Grid>
        </Grid>
      </DialogContent>

      <DialogActions data-testid="fertPlanTaskDialogActions" sx={{ px: 3, py: 2 }}>
        <Button onClick={onCloseTaskDialog} variant="outlined" data-testid="fertPlanTaskCancelBtn" sx={{ borderRadius: 2 }}>
          Abbrechen
        </Button>
        <Button onClick={onSaveTaskDraft} variant="contained" data-testid="fertPlanTaskSaveBtn" sx={{ borderRadius: 2 }}>
          Speichern
        </Button>
      </DialogActions>
    </Dialog>
  );
}

/** -----------------------------
 *  Hauptseite: Düngeplan
 *  ----------------------------- */
export default function FertilizerPlanPage({ api: fertApi = defaultFertApi }) {
  const theme = useTheme();
  const frame = createCardFrameSx(theme);
  const { grow_setup, setGrowSetup } = useGrowSetup();
  const growSetups = MOCK_GROW_SETUPS;
  const templates = MOCK_TEMPLATES;

  const [selectedWeekIndex, setSelectedWeekIndex] = useState(4);
  const [selectedPhase, setSelectedPhase] = useState("Blüte");
  const [fertPlanViewMode, setFertPlanViewMode] = useState("timeline");

  const [filterOpenOnly, setFilterOpenOnly] = useState(false);
  const [filterTypeKeys, setFilterTypeKeys] = useState(new Set(EVENT_TYPES.map((t) => t.key)));

  const [selectedTemplateId, setSelectedTemplateId] = useState(templates[0]?.id || "");
  const [fertPlanEvents, setFertPlanEvents] = useState(MOCK_EVENTS);

  const [isTaskDialogOpen, setIsTaskDialogOpen] = useState(false);
  const [taskDraft, setTaskDraft] = useState(createEmptyDraft(grow_setup, selectedWeekIndex, selectedPhase));
  
  useEffect(() => {
  (async () => {
    const { tasks } = await fertApi.getTasks({
      grow_setup_id: grow_setup,
      week_index: selectedWeekIndex,
      phase: selectedPhase, // kann "Blüte" sein
      open_only: filterOpenOnly,
      types: Array.from(filterTypeKeys),
    });
    setFertPlanEvents(tasks); // tasks sind bereits UI-events
  })().catch(console.error);
}, [selectedWeekIndex, selectedPhase, filterOpenOnly, filterTypeKeys,grow_setup]);


  function createEmptyDraft(growSetupId, weekIndex, phase) {
    const now = new Date();
    const toLocalInput = (d) => {
      const pad = (n) => String(n).padStart(2, "0");
      return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(
        d.getMinutes()
      )}`;
    };

    return {
      id: null,
      grow_setup_id: growSetupId,
      date_iso_local: toLocalInput(now),
      type: "fertilizing",
      phase,
      week_index: weekIndex,
      water_l: 3.0,
      targets: { ec: 1.6, ph: 6.2 },
      recipe_lines: [],
      actuals: { ec: null, ph: null },
      note: "",
      done: false,
    };
  }

  const visibleEvents = useMemo(() => {
    return fertPlanEvents
      .filter((ev) => ev.grow_setup_id === grow_setup)
      .filter((ev) => ev.week_index === selectedWeekIndex)
      .filter((ev) => ev.phase === selectedPhase)
      .filter((ev) => (filterOpenOnly ? !ev.done : true))
      .filter((ev) => filterTypeKeys.has(ev.type))
      .sort((a, b) => new Date(a.date_iso).getTime() - new Date(b.date_iso).getTime());
  }, [fertPlanEvents, grow_setup, selectedWeekIndex, selectedPhase, filterOpenOnly, filterTypeKeys]);

  const todaySummary = useMemo(() => {
    const open = visibleEvents.filter((e) => !e.done);
    const next = open[0] || null;
    return { openCount: open.length, nextEvent: next };
  }, [visibleEvents]);

  const handleToggleEventDone = async (eventId) => {
  try {
    // nur wenn DB id
    if (!Number.isFinite(Number(eventId))) {
      // optional: lokale mock togglen
      setFertPlanEvents((prev) => prev.map((e) => (e.id === eventId ? { ...e, done: !e.done } : e)));
      return;
    }

    const { task } = await fertApi.toggleTaskDone(Number(eventId));
    setFertPlanEvents((prev) => prev.map((e) => (Number(e.id) === Number(task.id) ? task : e)));
  } catch (err) {
    console.error(err);
    alert(err?.message || "Toggle fehlgeschlagen");
  }
};


  const handleOpenCreateTask = () => {
    setTaskDraft(createEmptyDraft(grow_setup, selectedWeekIndex, selectedPhase));
    setIsTaskDialogOpen(true);
  };

  const handleOpenEditTask = (eventId) => {
    const ev = fertPlanEvents.find((e) => e.id === eventId);
    if (!ev) return;

    const d = new Date(ev.date_iso);
    const pad = (n) => String(n).padStart(2, "0");
    const date_iso_local = `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(
      d.getMinutes()
    )}`;

    setTaskDraft({
      ...ev,
      date_iso_local,
    });
    setIsTaskDialogOpen(true);
  };

  const handleSaveTaskDraft = async () => {
  try {
    // Wichtig: grow_setup_id muss die DB-Integer-ID sein!
    // Wenn du im Context ein Objekt hast: grow_setup?.id verwenden
    const growSetupId = grow_setup;

    const payload = {
      ...taskDraft,
      grow_setup_id: growSetupId,
      // date_iso lässt du weg – fertApi.createTask() konvertiert date_iso_local automatisch
      // (oder du setzt sie hier selbst)
    };

    let saved;
    if (taskDraft.id && Number.isFinite(Number(taskDraft.id))) {
      // Update (nur wenn Task schon aus DB kommt und eine int id hat)
      const res = await fertApi.updateTask(Number(taskDraft.id), payload);
      saved = res.task;
    } else {
      // Create
      const res = await fertApi.createTask(payload);
      saved = res.task;
    }

    // lokalen State mit Server-Result synchronisieren
    setFertPlanEvents((prev) => {
      const exists = prev.some((e) => Number(e.id) === Number(saved.id));
      return exists
        ? prev.map((e) => (Number(e.id) === Number(saved.id) ? saved : e))
        : [...prev, saved];
    });

    setIsTaskDialogOpen(false);
  } catch (err) {
    console.error(err);
    alert(err?.message || "Speichern fehlgeschlagen");
  }
};


  const handleToggleFilterType = (typeKey) => {
    setFilterTypeKeys((prev) => {
      const next = new Set(prev);
      if (next.has(typeKey)) next.delete(typeKey);
      else next.add(typeKey);
      return next;
    });
  };

  return (
    <Box
      sx={{
        p: 2,
        minHeight: "100vh",
        background: `radial-gradient(1100px 420px at 12% 0%, ${alpha(
          theme.palette.primary.main,
          0.18
        )}, transparent 55%),
                     radial-gradient(900px 360px at 90% 10%, ${alpha(theme.palette.success.main, 0.14)}, transparent 55%),
                     radial-gradient(1000px 420px at 60% 100%, ${alpha(theme.palette.info.main, 0.12)}, transparent 55%),
                     ${theme.palette.background.default}`,
      }}
      data-testid="fertPlanPageRoot"
    >
      {/* HEADER / TOP BAR */}
      <Card
        variant="outlined"
        sx={{
          mb: 2,
          ...frame,
          ...createSectionGlowSx(theme, theme.palette.primary.main),
          background: `linear-gradient(135deg,
            ${alpha(theme.palette.primary.main, 0.18)},
            ${alpha(theme.palette.background.paper, 0.82)} 40%,
            ${alpha(theme.palette.success.main, 0.12)})`,
        }}
        data-testid="fertPlanHeaderCard"
      >
        <CardContent>
          <Grid container spacing={2} alignItems="center">
            <Grid item xs={12} md={4}>
              <Stack spacing={0.5} data-testid="fertPlanHeaderTitleStack">
                <Typography variant="h5" data-testid="fertPlanHeaderTitle" sx={{ fontWeight: 800, letterSpacing: 0.2 }}>
                  Düngeplan
                </Typography>
                <Typography variant="body2" color="text.secondary" data-testid="fertPlanHeaderSubtitle">
                  Planen + dokumentieren (EC/pH, Rezepte, Notizen)
                </Typography>
              </Stack>
            </Grid>



            <Grid item xs={6} md={2}>
              <TextField
                label="Woche"
                type="number"
                fullWidth
                value={selectedWeekIndex}
                onChange={(e) => setSelectedWeekIndex(Number(e.target.value))}
                inputProps={{ min: 1, max: 20, "data-testid": "fertPlanWeekSelect" }}
                sx={{ "& .MuiOutlinedInput-root": { borderRadius: 2 } }}
              />
            </Grid>

            <Grid item xs={6} md={3}>
              <FormControl fullWidth data-testid="fertPlanPhaseSelect">
                <InputLabel id="fertPlanPhaseSelectLabel">Phase</InputLabel>
                <Select
                  labelId="fertPlanPhaseSelectLabel"
                  label="Phase"
                  value={selectedPhase}
                  onChange={(e) => setSelectedPhase(e.target.value)}
                  sx={{ borderRadius: 2 }}
                >
                  {MOCK_PHASES.map((p) => (
                    <MenuItem key={p} value={p}>
                      {p}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>

            <Grid item xs={12}>
              <Stack direction="row" alignItems="center" justifyContent="space-between" spacing={2}>
                <Tabs
                  value={fertPlanViewMode}
                  onChange={(_, v) => setFertPlanViewMode(v)}
                  aria-label="Düngeplan Ansicht"
                  data-testid="fertPlanViewModeTabs"
                  sx={{
                    "& .MuiTabs-indicator": {
                      height: 4,
                      borderRadius: 2,
                      background: theme.palette.primary.main,
                    },
                    "& .MuiTab-root": { minHeight: 44, borderRadius: 2 },
                  }}
                >
                  <Tab
                    value="calendar"
                    icon={<CalendarMonthIcon />}
                    iconPosition="start"
                    label="Kalender"
                    data-testid="fertPlanTabCalendar"
                  />
                  <Tab
                    value="timeline"
                    icon={<ViewTimelineIcon />}
                    iconPosition="start"
                    label="Timeline"
                    data-testid="fertPlanTabTimeline"
                  />
                  <Tab
                    value="table"
                    icon={<TableRowsIcon />}
                    iconPosition="start"
                    label="Tabelle"
                    data-testid="fertPlanTabTable"
                  />
                </Tabs>

                <Button
                  variant="contained"
                  startIcon={<AddIcon />}
                  onClick={handleOpenCreateTask}
                  data-testid="fertPlanCreateTaskBtn"
                  sx={{
                    borderRadius: 2,
                    boxShadow: `0 12px 28px ${alpha(theme.palette.primary.main, 0.25)}`,
                  }}
                >
                  Task hinzufügen
                </Button>
              </Stack>
            </Grid>

            <Grid item xs={12}>
              <Stack direction="row" spacing={1} alignItems="center" data-testid="fertPlanHeaderChipsRow" sx={{ flexWrap: "wrap" }}>
                <Chip
                  label={`Phase: ${selectedPhase}`}
                  data-testid="fertPlanChipPhase"
                  sx={{
                    borderRadius: 2,
                    fontWeight: 700,
                    background: alpha(theme.palette.primary.main, 0.12),
                    border: `1px solid ${alpha(theme.palette.primary.main, 0.22)}`,
                  }}
                />
                <Chip
                  label={`Woche: ${selectedWeekIndex}`}
                  data-testid="fertPlanChipWeek"
                  sx={{
                    borderRadius: 2,
                    fontWeight: 700,
                    background: alpha(theme.palette.success.main, 0.10),
                    border: `1px solid ${alpha(theme.palette.success.main, 0.22)}`,
                  }}
                />
                <Chip
                  label="Zielwerte: EC/pH"
                  variant="outlined"
                  data-testid="fertPlanChipTargetsHint"
                  sx={{ borderRadius: 2 }}
                />
              </Stack>
            </Grid>
          </Grid>
        </CardContent>
      </Card>

      {/* MAIN LAYOUT */}
      <Grid container spacing={2} data-testid="fertPlanMainLayout">
        {/* SIDEBAR */}
        <Grid item xs={12} md={3} data-testid="fertPlanSidebar">
          <Card
            variant="outlined"
            sx={{ mb: 2, ...frame, ...createSectionGlowSx(theme, theme.palette.info.main) }}
            data-testid="fertPlanTodaySummaryCard"
          >
            <CardHeader title="Heute & Nächste Schritte" data-testid="fertPlanTodaySummaryTitle" />
            <CardContent>
              <Stack spacing={1}>
                <Typography variant="body2" data-testid="fertPlanTodayOpenCount">
                  Offene Tasks (Auswahl):{" "}
                  <Box component="span" sx={{ fontWeight: 900, color: theme.palette.warning.main }}>
                    {todaySummary.openCount}
                  </Box>
                </Typography>
                <Divider />
                {todaySummary.nextEvent ? (
                  <Stack spacing={0.5} data-testid="fertPlanNextEventBox">
                    <Typography variant="subtitle2" data-testid="fertPlanNextEventTitle" sx={{ fontWeight: 800 }}>
                      Nächster Task
                    </Typography>
                    <Typography variant="body2" data-testid="fertPlanNextEventWhen">
                      {formatDateTimeLocal(todaySummary.nextEvent.date_iso)}
                    </Typography>
                    <Typography variant="body2" data-testid="fertPlanNextEventType">
                      {getTypeMeta(todaySummary.nextEvent.type).label}
                    </Typography>
                    <Button
                      variant="outlined"
                      onClick={() => handleOpenEditTask(todaySummary.nextEvent.id)}
                      data-testid="fertPlanNextEventEditBtn"
                      sx={{ mt: 1, borderRadius: 2 }}
                    >
                      Öffnen
                    </Button>
                  </Stack>
                ) : (
                  <Typography variant="body2" color="text.secondary" data-testid="fertPlanNoNextEventHint">
                    Keine offenen Tasks in dieser Auswahl.
                  </Typography>
                )}
              </Stack>
            </CardContent>
          </Card>

          <Card
            variant="outlined"
            sx={{ mb: 2, ...frame, ...createSectionGlowSx(theme, theme.palette.secondary.main) }}
            data-testid="fertPlanFiltersCard"
          >
            <CardHeader title="Filter" data-testid="fertPlanFiltersTitle" />
            <CardContent>
              <FormGroup data-testid="fertPlanFiltersFormGroup">
                <FormControlLabel
                  control={
                    <Switch
                      checked={filterOpenOnly}
                      onChange={(e) => setFilterOpenOnly(e.target.checked)}
                      inputProps={{ "data-testid": "fertPlanFilterOpenOnlySwitch" }}
                    />
                  }
                  label="Nur offene Tasks"
                  data-testid="fertPlanFilterOpenOnlyRow"
                  sx={{
                    borderRadius: 2,
                    px: 1,
                    py: 0.5,
                    background: alpha(theme.palette.secondary.main, 0.08),
                    border: `1px solid ${alpha(theme.palette.secondary.main, 0.18)}`,
                  }}
                />

                <Divider sx={{ my: 1 }} />

                <Typography variant="subtitle2" data-testid="fertPlanFilterTypesLabel" sx={{ fontWeight: 800 }}>
                  Typen
                </Typography>

                {EVENT_TYPES.map((t) => (
                  <FormControlLabel
                    key={t.key}
                    control={
                      <Checkbox
                        checked={filterTypeKeys.has(t.key)}
                        onChange={() => handleToggleFilterType(t.key)}
                        inputProps={{ "data-testid": `fertPlanFilterType_${t.key}` }}
                      />
                    }
                    label={t.label}
                    data-testid={`fertPlanFilterTypeRow_${t.key}`}
                    sx={{
                      mt: 0.25,
                      borderRadius: 2,
                      px: 0.5,
                      "&:hover": { background: alpha(getTypeAccent(theme, t.key), 0.08) },
                    }}
                  />
                ))}
              </FormGroup>
            </CardContent>
          </Card>

          <Card
            variant="outlined"
            sx={{ ...frame, ...createSectionGlowSx(theme, theme.palette.primary.main) }}
            data-testid="fertPlanTemplatesCard"
          >
            <CardHeader
              title="Templates"
              subheader="Später: Plan automatisch generieren"
              data-testid="fertPlanTemplatesTitle"
            />
            <CardContent>
              <FormControl fullWidth data-testid="fertPlanTemplateSelect">
                <InputLabel id="fertPlanTemplateSelectLabel">Template</InputLabel>
                <Select
                  labelId="fertPlanTemplateSelectLabel"
                  label="Template"
                  value={selectedTemplateId}
                  onChange={(e) => setSelectedTemplateId(e.target.value)}
                  sx={{ borderRadius: 2 }}
                >
                  {templates.map((tpl) => (
                    <MenuItem key={tpl.id} value={tpl.id}>
                      {tpl.name}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>

              <Divider sx={{ my: 1.5 }} />

              <Typography variant="body2" color="text.secondary" data-testid="fertPlanTemplateDescription">
                {templates.find((t) => t.id === selectedTemplateId)?.description || "—"}
              </Typography>

              <Stack direction="row" spacing={1} sx={{ mt: 2 }}>
                <Button
                  variant="outlined"
                  fullWidth
                  data-testid="fertPlanTemplateApplyBtn"
                  onClick={() => alert("TODO: Template anwenden (API) – hier nur Platzhalter")}
                  sx={{ borderRadius: 2 }}
                >
                  Anwenden
                </Button>
                <Button
                  variant="outlined"
                  fullWidth
                  data-testid="fertPlanTemplateManageBtn"
                  onClick={() => alert("TODO: Template verwalten – Platzhalter")}
                  sx={{ borderRadius: 2 }}
                >
                  Verwalten
                </Button>
              </Stack>
            </CardContent>
          </Card>
        </Grid>

        {/* CONTENT */}
        <Grid item xs={12} md={9} data-testid="fertPlanContentArea">
          <Card variant="outlined" data-testid="fertPlanContentCard" sx={{ ...frame, ...createSectionGlowSx(theme, theme.palette.primary.main) }}>
            <CardHeader
              title={
                <Stack direction="row" alignItems="center" spacing={1} data-testid="fertPlanContentTitleRow">
                  <Typography variant="h6" data-testid="fertPlanContentTitle" sx={{ fontWeight: 900 }}>
                    {fertPlanViewMode === "timeline"
                      ? "Timeline"
                      : fertPlanViewMode === "calendar"
                      ? "Kalender (Platzhalter)"
                      : "Tabelle (Platzhalter)"}
                  </Typography>
                  <Badge
                    badgeContent={visibleEvents.length}
                    color="primary"
                    data-testid="fertPlanVisibleEventsCountBadge"
                    sx={{
                      "& .MuiBadge-badge": {
                        borderRadius: 2,
                        boxShadow: `0 10px 20px ${alpha(theme.palette.primary.main, 0.25)}`,
                      },
                    }}
                  />
                </Stack>
              }
              subheader="Tasks als Cards – mit Rezept, Ziel/IST und Status"
              data-testid="fertPlanContentSubheader"
            />
            <Divider />

            <CardContent data-testid="fertPlanContentBody">
              {fertPlanViewMode !== "timeline" ? (
                <Typography color="text.secondary" data-testid="fertPlanPlaceholderNonTimeline">
                  Diese Ansicht ist als Platzhalter angelegt. Du kannst hier später:
                  <br />• Kalender: Wochen-/Monatsgrid + Tages-Sidebar
                  <br />• Tabelle: Export/CSV + schnelle Bearbeitung
                </Typography>
              ) : (
                <Stack spacing={2} data-testid="fertPlanTimelineList">
                  {visibleEvents.length === 0 ? (
                    <Typography color="text.secondary" data-testid="fertPlanTimelineEmptyHint">
                      Keine Tasks für die aktuelle Auswahl.
                    </Typography>
                  ) : (
                    visibleEvents.map((ev) => (
                      <TimelineEventCard
                        key={ev.id}
                        ev={ev}
                        onEdit={handleOpenEditTask}
                        onToggleDone={handleToggleEventDone}
                      />
                    ))
                  )}
                </Stack>
              )}
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      <FertilizerTaskDialog
        isTaskDialogOpen={isTaskDialogOpen}
        onCloseTaskDialog={() => setIsTaskDialogOpen(false)}
        taskDraft={taskDraft}
        setTaskDraft={setTaskDraft}
        onSaveTaskDraft={handleSaveTaskDraft}
      />
    </Box>
  );
}

/** -----------------------------
 *  Timeline Card (ein Event)
 *  ----------------------------- */
function TimelineEventCard({ ev, onEdit, onToggleDone }) {
  const theme = useTheme();
  const frame = createCardFrameSx(theme);

  const accent = getTypeAccent(theme, ev.type);
  const typeMeta = getTypeMeta(ev.type);
  const recipeTotals = computeRecipeTotals(ev.recipe_lines || [], ev.water_l);
  const hasRecipe = (ev.recipe_lines || []).length > 0;

  return (
    <Card
      variant="outlined"
      data-testid={`fertPlanEventCard_${ev.id}`}
      sx={{
        ...frame,
        borderLeft: `7px solid ${alpha(accent, 0.95)}`,
        opacity: ev.done ? 0.78 : 1,
        background: `linear-gradient(180deg, ${alpha(accent, 0.06)}, ${alpha(theme.palette.background.paper, 0.84)})`,
      }}
    >
      <CardContent>
        <Stack direction="row" justifyContent="space-between" alignItems="flex-start" spacing={2}>
          <Box sx={{ flex: 1 }} data-testid={`fertPlanEventMeta_${ev.id}`}>
            <Stack direction="row" spacing={1} alignItems="center" data-testid={`fertPlanEventHeaderRow_${ev.id}`} sx={{ flexWrap: "wrap" }}>
              <Chip
                icon={typeMeta.icon}
                label={typeMeta.label}
                size="small"
                data-testid={`fertPlanEventTypeChip_${ev.id}`}
                sx={{
                  borderRadius: 2,
                  fontWeight: 800,
                  background: alpha(accent, 0.14),
                  border: `1px solid ${alpha(accent, 0.28)}`,
                }}
              />
              <Chip
                label={`Phase: ${ev.phase}`}
                size="small"
                variant="outlined"
                data-testid={`fertPlanEventPhaseChip_${ev.id}`}
                sx={{ borderRadius: 2 }}
              />
              <Chip
                label={`Woche: ${ev.week_index}`}
                size="small"
                variant="outlined"
                data-testid={`fertPlanEventWeekChip_${ev.id}`}
                sx={{ borderRadius: 2 }}
              />
              {ev.done ? (
                <Chip
                  icon={<CheckCircleIcon fontSize="small" />}
                  label="Erledigt"
                  size="small"
                  data-testid={`fertPlanEventDoneChip_${ev.id}`}
                  sx={{
                    borderRadius: 2,
                    background: alpha(theme.palette.success.main, 0.12),
                    border: `1px solid ${alpha(theme.palette.success.main, 0.22)}`,
                  }}
                />
              ) : (
                <Chip
                  label="Offen"
                  size="small"
                  variant="outlined"
                  data-testid={`fertPlanEventOpenChip_${ev.id}`}
                  sx={{
                    borderRadius: 2,
                    background: alpha(theme.palette.warning.main, 0.10),
                    border: `1px solid ${alpha(theme.palette.warning.main, 0.25)}`,
                    fontWeight: 800,
                  }}
                />
              )}
            </Stack>

            <Typography variant="subtitle1" sx={{ mt: 1, fontWeight: 900 }} data-testid={`fertPlanEventDate_${ev.id}`}>
              {formatDateTimeLocal(ev.date_iso)}
            </Typography>

            <Stack direction="row" spacing={2} sx={{ mt: 1, flexWrap: "wrap" }} data-testid={`fertPlanEventTargetsRow_${ev.id}`}>
              <Typography variant="body2" data-testid={`fertPlanEventTargetEC_${ev.id}`}>
                Ziel EC: <b>{ev.targets?.ec ?? "—"}</b>
              </Typography>
              <Typography variant="body2" data-testid={`fertPlanEventTargetPH_${ev.id}`}>
                Ziel pH: <b>{ev.targets?.ph ?? "—"}</b>
              </Typography>
              <Typography variant="body2" data-testid={`fertPlanEventWater_${ev.id}`}>
                Wasser: <b>{ev.water_l ?? "—"}</b>
                {ev.water_l != null ? " L" : ""}
              </Typography>
            </Stack>

            <Stack direction="row" spacing={2} sx={{ mt: 1, flexWrap: "wrap" }} data-testid={`fertPlanEventActualsRow_${ev.id}`}>
              <Typography variant="body2" data-testid={`fertPlanEventActualEC_${ev.id}`}>
                IST EC: <b>{ev.actuals?.ec ?? "—"}</b>
              </Typography>
              <Typography variant="body2" data-testid={`fertPlanEventActualPH_${ev.id}`}>
                IST pH: <b>{ev.actuals?.ph ?? "—"}</b>
              </Typography>
            </Stack>

            <Divider sx={{ my: 1.5 }} />

            <Stack spacing={0.5} data-testid={`fertPlanEventRecipeSection_${ev.id}`}>
              <Typography variant="subtitle2" data-testid={`fertPlanEventRecipeTitle_${ev.id}`} sx={{ fontWeight: 900 }}>
                Rezept
              </Typography>

              {!hasRecipe ? (
                <Typography variant="body2" color="text.secondary" data-testid={`fertPlanEventRecipeEmpty_${ev.id}`}>
                  Kein Rezept hinterlegt.
                </Typography>
              ) : (
                <List dense disablePadding data-testid={`fertPlanEventRecipeList_${ev.id}`}>
                {recipeTotals.map((line) => {
                    const buyUrl = getBuyUrl(line.product_name);

                    return (
                    <ListItemButton
                        key={line.id}
                        sx={{
                        borderRadius: 2,
                        mb: 0.5,
                        border: `1px solid ${alpha(theme.palette.divider, 0.75)}`,
                        background: alpha(theme.palette.background.paper, 0.6),
                        "&:hover": {
                            borderColor: alpha(accent, 0.5),
                            background: alpha(accent, 0.06),
                        },
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "space-between",
                        gap: 1,
                        }}
                        data-testid={`fertPlanEventRecipeLine_${ev.id}_${line.id}`}
                    >
                        <ListItemText
                        primary={`${line.product_name || "(Produkt)"} — ${line.ml_per_l} ml/L`}
                        secondary={ev.water_l ? `Gesamt: ${line.total_ml} ml` : "Gesamt: —"}
                        primaryTypographyProps={{ variant: "body2", fontWeight: 700 }}
                        secondaryTypographyProps={{ variant: "caption" }}
                        sx={{ mr: 1 }}
                        />

                        {buyUrl ? (
                        <Button
                            component="a"
                            href={buyUrl}
                            target="_blank"
                            rel="noopener noreferrer"
                            onClick={(e) => e.stopPropagation()} // verhindert, dass der ListItemButton “mitklickt”
                            size="small"
                            variant="contained"
                            sx={{
                            borderRadius: 999,
                            px: 2,
                            fontWeight: 800,
                            textTransform: "none",
                            backgroundColor: theme.palette.success.main,
                            "&:hover": { backgroundColor: theme.palette.success.dark },
                            boxShadow: `0 10px 22px ${alpha(theme.palette.success.main, 0.25)}`,
                            whiteSpace: "nowrap",
                            }}
                            data-testid={`fertPlanEventRecipeBuyBtn_${ev.id}_${line.id}`}
                        >
                            Kaufen
                        </Button>
                        ) : null}
                    </ListItemButton>
                    );
                })}
                </List>

              )}
            </Stack>

            {ev.note ? (
              <>
                <Divider sx={{ my: 1.5 }} />
                <Typography
                  variant="body2"
                  data-testid={`fertPlanEventNote_${ev.id}`}
                  sx={{
                    p: 1,
                    borderRadius: 2,
                    background: alpha(theme.palette.primary.main, 0.06),
                    border: `1px solid ${alpha(theme.palette.primary.main, 0.14)}`,
                  }}
                >
                  <b>Notiz:</b> {ev.note}
                </Typography>
              </>
            ) : null}
          </Box>

          <Stack spacing={1} alignItems="stretch" data-testid={`fertPlanEventActions_${ev.id}`} sx={{ minWidth: 190 }}>
            <Button
              variant="outlined"
              startIcon={<EditIcon />}
              onClick={() => onEdit(ev.id)}
              data-testid={`fertPlanEventEditBtn_${ev.id}`}
              sx={{ borderRadius: 2 }}
            >
              Bearbeiten
            </Button>

            <Button
              variant={ev.done ? "outlined" : "contained"}
              startIcon={<CheckCircleIcon />}
              onClick={() => onToggleDone(ev.id)}
              data-testid={`fertPlanEventToggleDoneBtn_${ev.id}`}
              sx={{
                borderRadius: 2,
                boxShadow: ev.done ? "none" : `0 12px 26px ${alpha(theme.palette.success.main, 0.22)}`,
              }}
            >
              {ev.done ? "Wieder öffnen" : "Als erledigt"}
            </Button>

            <Tooltip title="Später: Rezept als Schritt-für-Schritt Flow öffnen (Wasser → Produkte → pH)">
              <span>
                <Button
                  variant="outlined"
                  disabled={!hasRecipe}
                  data-testid={`fertPlanEventOpenMixFlowBtn_${ev.id}`}
                  sx={{ borderRadius: 2 }}
                >
                  Nährlösung ansetzen
                </Button>
              </span>
            </Tooltip>
          </Stack>
        </Stack>
      </CardContent>
    </Card>
  );
}
